import React from 'react';
import { Task, TaskStatus } from '../types';
import WeeklyChart from './WeeklyChart';

interface DailyStatsProps {
  todayTasks: Task[];
  weekTasks: Task[];
  activeStatus?: TaskStatus | 'All';
  onStatusClick?: (status: TaskStatus | 'All') => void;
}

function getWeekDays() {
  const days = [];
  const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push({
      label: labels[d.getDay()],
      date: d.toISOString().slice(0, 10),
      total: 0,
      completed: 0,
    });
  }
  return days;
}

export default function DailyStats({ todayTasks, weekTasks, activeStatus = 'All', onStatusClick }: DailyStatsProps) {
  const total = todayTasks.length;
  const completed = todayTasks.filter(t => t.status === 'Completed').length;
  const inProgress = todayTasks.filter(t => t.status === 'In Progress').length;
  const pending = todayTasks.filter(t => t.status === 'Pending').length;
  const remaining = pending + inProgress;
  const handledToday = completed + inProgress;
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
  const avgProgress = total === 0 ? 0 : Math.round(todayTasks.reduce((sum, t) => sum + t.progress, 0) / total);

  const now = Date.now();
  const overdue = todayTasks.filter(t => t.deadline && t.status !== 'Completed' && new Date(t.deadline).getTime() < now).length;
  const dueSoon = todayTasks.filter(t => {
    if (!t.deadline || t.status === 'Completed') return false;
    const mins = Math.floor((new Date(t.deadline).getTime() - now) / 60000);
    return mins >= 0 && mins <= 120;
  }).length;

  const topCategory = (() => {
    if (todayTasks.length === 0) return 'None';
    const map = new Map<string, number>();
    todayTasks.forEach((t) => map.set(t.category, (map.get(t.category) ?? 0) + 1));
    const entries = Array.from(map.entries());
    return entries.sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'None';
  })();

  const feedback = (() => {
    if (total === 0) return null;
    if (pct === 100) return { msg: '🎉 Outstanding! You completed all tasks today!', cls: 'feedback-perfect' };
    if (pct >= 80) return { msg: `🔥 Great work! You've completed ${pct}% of today's tasks!`, cls: 'feedback-great' };
    if (pct >= 50) return { msg: `👍 Good progress! ${pct}% done — keep it up!`, cls: 'feedback-good' };
    if (pct > 0) return { msg: `💪 You've started — ${pct}% done so far. Push through!`, cls: 'feedback-ok' };
    return null;
  })();

  // Build week stats
  const weekDays = getWeekDays();
  weekTasks.forEach(task => {
    const day = weekDays.find(d => d.date === task.task_date);
    if (day) {
      day.total++;
      if (task.status === 'Completed') day.completed++;
    }
  });

  const avgCompletion = (() => {
    const days = weekDays.filter(d => d.total > 0);
    if (days.length === 0) return 0;
    const sum = days.reduce((acc, d) => acc + (d.completed / d.total) * 100, 0);
    return Math.round(sum / days.length);
  })();

  return (
    <div className="daily-stats">
      <h2 className="stats-section-title">Today's Performance</h2>

      <div className="today-kpi-grid">
        <button
          className={`today-kpi-card ${activeStatus === 'All' ? 'active' : ''}`}
          onClick={() => onStatusClick?.('All')}
        >
          <div className="today-kpi-label">Total Tasks</div>
          <div className="today-kpi-value">{total}</div>
        </button>
        <button
          className={`today-kpi-card ${activeStatus === 'In Progress' ? 'active' : ''}`}
          onClick={() => onStatusClick?.('In Progress')}
        >
          <div className="today-kpi-label">Handled Today</div>
          <div className="today-kpi-value">{handledToday}</div>
        </button>
        <button
          className={`today-kpi-card ${activeStatus === 'Pending' ? 'active' : ''}`}
          onClick={() => onStatusClick?.('Pending')}
        >
          <div className="today-kpi-label">Remaining</div>
          <div className="today-kpi-value">{remaining}</div>
        </button>
        <button
          className={`today-kpi-card ${activeStatus === 'Completed' ? 'active' : ''}`}
          onClick={() => onStatusClick?.('Completed')}
        >
          <div className="today-kpi-label">Completed</div>
          <div className="today-kpi-value">{completed}</div>
        </button>
      </div>

      <div className="today-secondary-metrics">
        <span>Completion: <strong>{pct}%</strong></span>
        <span>Average progress: <strong>{avgProgress}%</strong></span>
        <span>Due soon: <strong>{dueSoon}</strong></span>
        <span>Overdue: <strong>{overdue}</strong></span>
        <span>Top category: <strong>{topCategory}</strong></span>
      </div>

      {feedback && (
        <div className={`performance-feedback ${feedback.cls}`}>
          {feedback.msg}
        </div>
      )}

      <div className="today-summary-row">
        <div className="summary-ring-wrap">
          <svg viewBox="0 0 36 36" className="summary-ring">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--color-border)" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15.9" fill="none"
              stroke={pct === 100 ? 'var(--color-accent)' : 'var(--color-primary)'}
              strokeWidth="3"
              strokeDasharray={`${pct} ${100 - pct}`}
              strokeDashoffset="25"
              strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
            <text x="18" y="20.5" textAnchor="middle" fontSize="8" fontWeight="700" fill="currentColor">{pct}%</text>
          </svg>
        </div>

        <div className="today-breakdown">
          <div className="breakdown-item">
            <span className="breakdown-dot dot-completed" />
            <span className="breakdown-label">Completed</span>
            <span className="breakdown-count">{completed}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-dot dot-in-progress" />
            <span className="breakdown-label">In Progress</span>
            <span className="breakdown-count">{inProgress}</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-dot dot-pending" />
            <span className="breakdown-label">Pending</span>
            <span className="breakdown-count">{pending}</span>
          </div>
        </div>
      </div>

      <h2 className="stats-section-title" style={{ marginTop: 28 }}>
        This Week
        {avgCompletion > 0 && (
          <span className="week-avg-badge">{avgCompletion}% avg</span>
        )}
      </h2>
      <WeeklyChart stats={weekDays} />
    </div>
  );
}
